import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ChangeGroupsFilterParams,
  GondolaChangeGroupsDataSource,
  GondolaChangeGroupsData,
  SKUSectionChangeGroup,
  ClosestGondolaDistItem,
} from "@/entities/gondola-change-groups/model/types";
import { FILTER_OPTIONS } from "@/features/gondola-change-groups/lib/const";
import { SKUBaseId } from "@/entities/gondola-change-groups/model/types";

export interface TrackAvatarBinding {
  trackId: number;
  animalName: string;
  color: string;
}

export interface JourneyTrackRelation {
  journeyId: string;
  trackIds: number[];
  recoveredTrackIds: number[];
}

export interface GondolaChangeGroupsState {
  filters: ChangeGroupsFilterParams;
  dataSource: GondolaChangeGroupsDataSource | null;
  fileData: GondolaChangeGroupsData | null;
  selectedGondolaChangeGroupId: string | null;
  selectedGondolaChangeId: string | null;
  selectedSkuSectionChangeId: string | null;
  selectedDetectionId: string | null;
  selectedTriangulationId: string | null;
  selectedSkuSectionChangeGroupTableData: SKUSectionChangeGroup[] | null;
  trackAvatarBindings: Record<number, TrackAvatarBinding>;
  journeyTrackRelations: JourneyTrackRelation[];
  trackIdsInOcclusion: number[];
  recoveredTrackIdMapping: Record<number, number>;
  visualizeTrackProjections: boolean;
  closestGondolaDistItems: ClosestGondolaDistItem[];
  selectedClosestGondolaDistItem: ClosestGondolaDistItem | null;
}

const initialState: GondolaChangeGroupsState = {
  filters: {
    selectedSkuBaseId: undefined,
    acceptedSkuSectionGroups: {
      option: FILTER_OPTIONS.ALL,
      value: undefined,
    },
    intersectedAnnotations: {
      option: FILTER_OPTIONS.ALL,
      value: undefined,
    },
    intersectedProxs: {
      option: FILTER_OPTIONS.ALL,
      value: undefined,
    },
  },
  dataSource: null,
  fileData: null,
  selectedGondolaChangeGroupId: null,
  selectedGondolaChangeId: null,
  selectedSkuSectionChangeId: null,
  selectedDetectionId: null,
  selectedTriangulationId: null,
  selectedSkuSectionChangeGroupTableData: null,
  trackAvatarBindings: {},
  journeyTrackRelations: [],
  trackIdsInOcclusion: [],
  recoveredTrackIdMapping: {},
  visualizeTrackProjections: true,
  closestGondolaDistItems: [],
  selectedClosestGondolaDistItem: null,
};

export const gondolaChangeGroupsSlice = createSlice({
  name: "gondolaChangeGroups",
  initialState,
  selectors: {
    selectFilters: (state) => state.filters,
    selectSelectedGondolaChangeGroupId: (state) =>
      state.selectedGondolaChangeGroupId,
    selectSelectedGondolaChangeId: (state) => state.selectedGondolaChangeId,
    selectSelectedSkuSectionChangeId: (state) =>
      state.selectedSkuSectionChangeId,
    selectSelectedDetectionId: (state) => state.selectedDetectionId,
    selectSelectedTriangulationId: (state) => state.selectedTriangulationId,
    selectSelectedSkuSectionChangeGroupTableData: (state) =>
      state.selectedSkuSectionChangeGroupTableData,
    selectDataSource: (state) => state.dataSource,
    selectFileData: (state) => state.fileData,
    selectTrackAvatarBindings: (state) => state.trackAvatarBindings,
    selectJourneyTrackRelations: (state) => state.journeyTrackRelations,
    selectTrackIdsInOcclusion: (state) => state.trackIdsInOcclusion,
    selectRecoveredTrackIdMapping: (state) => state.recoveredTrackIdMapping,
    selectVisualizeTrackProjections: (state) => state.visualizeTrackProjections,
    selectClosestGondolaDistItems: (state) => state.closestGondolaDistItems,
    selectSelectedClosestGondolaDistItem: (state) =>
      state.selectedClosestGondolaDistItem,
  },
  reducers: {
    setFilters(state, action: PayloadAction<ChangeGroupsFilterParams>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters(state) {
      state.filters = initialState.filters;
    },
    setSelectedSkuBaseId(state, action: PayloadAction<SKUBaseId | null>) {
      state.filters.selectedSkuBaseId = action.payload || undefined;
    },
    clearSelectedSkuBaseId(state) {
      state.filters.selectedSkuBaseId = undefined;
    },

    setDataSource(
      state,
      action: PayloadAction<GondolaChangeGroupsDataSource | null>
    ) {
      state.dataSource = action.payload;
    },
    setFileData(state, action: PayloadAction<GondolaChangeGroupsData | null>) {
      state.fileData = action.payload;
    },
    clearDataSource(state) {
      state.dataSource = null;
      state.fileData = null;
    },

    setSelectedGondolaChangeGroupId(
      state,
      action: PayloadAction<string | null>
    ) {
      state.selectedGondolaChangeGroupId = action.payload;
      state.selectedGondolaChangeId = null;
    },

    setSelectedGondolaChangeId(state, action: PayloadAction<string | null>) {
      state.selectedGondolaChangeId = action.payload;
    },

    setSelectedSkuSectionChangeId(state, action: PayloadAction<string | null>) {
      state.selectedSkuSectionChangeId = action.payload;
    },

    setSelectedDetectionId(state, action: PayloadAction<string | null>) {
      state.selectedDetectionId = action.payload;
    },

    setSelectedTriangulationId(state, action: PayloadAction<string | null>) {
      state.selectedTriangulationId = action.payload;
    },

    setSelectedSkuSectionChangeGroupTableData(
      state,
      action: PayloadAction<SKUSectionChangeGroup[] | null>
    ) {
      state.selectedSkuSectionChangeGroupTableData = action.payload;
    },

    setTrackAvatarBindings(
      state,
      action: PayloadAction<Record<number, TrackAvatarBinding>>
    ) {
      state.trackAvatarBindings = action.payload;
    },

    setJourneyTrackRelations(
      state,
      action: PayloadAction<JourneyTrackRelation[]>
    ) {
      state.journeyTrackRelations = action.payload;
    },

    setTrackIdsInOcclusion(state, action: PayloadAction<number[]>) {
      state.trackIdsInOcclusion = action.payload;
    },

    setRecoveredTrackIdMapping(
      state,
      action: PayloadAction<Record<number, number>>
    ) {
      state.recoveredTrackIdMapping = action.payload;
    },

    setVisualizeTrackProjections(state, action: PayloadAction<boolean>) {
      state.visualizeTrackProjections = action.payload;
    },

    clearTrackData(state) {
      state.journeyTrackRelations = [];
      state.trackIdsInOcclusion = [];
      state.recoveredTrackIdMapping = {};
      state.trackAvatarBindings = {};
    },

    setClosestGondolaDistItems(
      state,
      action: PayloadAction<ClosestGondolaDistItem[]>
    ) {
      state.closestGondolaDistItems = action.payload;
    },

    setSelectedClosestGondolaDistItem(
      state,
      action: PayloadAction<ClosestGondolaDistItem | null>
    ) {
      state.selectedClosestGondolaDistItem = action.payload;
    },

    clearClosestGondolaDistItems(state) {
      state.closestGondolaDistItems = [];
      state.selectedClosestGondolaDistItem = null;
    },

    resetState(state) {
      const persistedFilters = state.filters;

      return {
        ...initialState,
        filters: persistedFilters,
      };
    },
    resetToInitial() {
      return initialState;
    },
  },
});

export const {
  setFilters,
  resetFilters,
  setSelectedSkuBaseId,
  clearSelectedSkuBaseId,
  setDataSource,
  setFileData,
  clearDataSource,
  setSelectedGondolaChangeGroupId,
  setSelectedGondolaChangeId,
  setSelectedSkuSectionChangeId,
  setSelectedDetectionId,
  setSelectedTriangulationId,
  setSelectedSkuSectionChangeGroupTableData,
  setTrackAvatarBindings,
  setJourneyTrackRelations,
  setTrackIdsInOcclusion,
  setRecoveredTrackIdMapping,
  setVisualizeTrackProjections,
  clearTrackData,
  setClosestGondolaDistItems,
  setSelectedClosestGondolaDistItem,
  clearClosestGondolaDistItems,
  resetState,
  resetToInitial,
} = gondolaChangeGroupsSlice.actions;

export const {
  selectFilters,
  selectSelectedGondolaChangeGroupId,
  selectSelectedGondolaChangeId,
  selectSelectedSkuSectionChangeId,
  selectSelectedDetectionId,
  selectSelectedTriangulationId,
  selectSelectedSkuSectionChangeGroupTableData,
  selectDataSource,
  selectFileData,
  selectTrackAvatarBindings,
  selectJourneyTrackRelations,
  selectTrackIdsInOcclusion,
  selectRecoveredTrackIdMapping,
  selectVisualizeTrackProjections,
  selectClosestGondolaDistItems,
  selectSelectedClosestGondolaDistItem,
} = gondolaChangeGroupsSlice.selectors;
